import { ChangeDetectionStrategy, Component, Input, computed, inject, signal } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { helpScreenFor } from '../../domain/skipper-help-registry';
import { SkipperPanelsService } from '../../engine/skipper-panels.service';
import { SkipperScreenService } from '../../engine/skipper-screen.service';
import { SkipperAvatarComponent } from '../skipper-avatar/skipper-avatar.component';

/**
 * Skipper's permanent perch on a screen: tap Skipper to be told what is on it.
 * Port of `SkipperHelpButton`.
 *
 * A button, not "long-press a feature to learn about it": long-press is already
 * spoken for (dropping a waypoint, message actions), and a gesture nobody is
 * told about is a feature nobody uses.
 *
 * Renders nothing when Skipper has no copy for the screen, so placing it
 * before the copy is written is safe.
 */
@Component({
  selector: 'app-skipper-help-button',
  standalone: true,
  imports: [SkipperAvatarComponent],
  template: `
    @if (screen()) {
      <button type="button" [class]="'perch ' + variant" [style.width.px]="size" [style.height.px]="size"
              aria-label="Ask Skipper about this screen" title="Ask Skipper about this screen" (click)="open()">
        <!-- Keeps the idle bob: at this scale it is what makes Skipper read as more than an icon. -->
        <app-skipper-avatar [size]="size * 0.72" mood="idle" />
      </button>
    }
  `,
  styleUrl: './skipper-help-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkipperHelpButtonComponent {
  private panels = inject(SkipperPanelsService);
  private currentScreen = inject(SkipperScreenService);

  /** Defaults to the screen the user is on. */
  @Input() set screenId(value: string | null) {
    this.explicitScreen.set(value);
  }

  @Input() size = 44;

  /**
   * `glass` matches the floating controls over the map; `flat` is the plain
   * disc used in headers on plain backgrounds.
   */
  @Input() variant: 'glass' | 'flat' = 'flat';

  private readonly explicitScreen = signal<string | null>(null);

  readonly screen = computed(() =>
    environment.skipperEnabled
      ? helpScreenFor(this.explicitScreen() ?? this.currentScreen.screenId())
      : null,
  );

  open(): void {
    void this.panels.openHelp(this.screen()?.screenId ?? null);
  }
}
